import { graphql, useStaticQuery } from 'gatsby'
import { isObject, isString, sortBy } from 'lodash'
import { PrismicRichText, PrismicText } from '@prismicio/react'
import moment from 'moment'
import Icon from './components/icon/icon'
import React from 'react'
import axios from 'axios'

export const useSiteMetadata = () => {
  const data = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          title
          description
          author
          twitterUsername
          image
          siteUrl
        }
      }
    }
  `)

  return data.site.siteMetadata
}

export function getText(d) {
  return d ? d?.map((o) => o.text).join('') : ''
}

/*
<div class="cognito">
<script src="https://www.cognitoforms.com/s/bIzPUHzgCkWRr3DDrj7bRg"></script>
<script>Cognito.load("forms", { id: "85" });</script>
</div>
*/
export function renderCognitoForm(formId) {
  const script = document.createElement('script')
  script.onload = () => {
    window.Cognito.load('forms', { id: formId })
  }
  script.src = `https://www.cognitoforms.com/s/bIzPUHzgCkWRr3DDrj7bRg`
  document.body.appendChild(script)
}

export function ColorToHex(s) {
  let hex = '#000000'

  const regex = /(^#[0-9A-F]{6}$)|(^#[0-9A-F]{3}$)/i

  switch (s) {
    case 'Zoo Green':
      hex = '#40AF49'
      break
    case 'Zoo Blue':
      hex = '#0082B5'
      break
    case 'Cardinal':
      hex = '#DB1C0A'
      break
    case 'Orchid':
      hex = '#AF4068'
      break
    case 'Tangerine':
      hex = '#FBA24F'
      break
    case 'Sea':
      hex = '#3BBC8E'
      break
    case 'Terra Cotta':
      hex = '#C6443E'
      break
    case 'Pumpkin':
      hex = '#FD6743'
      break
    case 'Canary':
      hex = '#FDC843'
      break
    case 'White':
      hex = '#FFF'
      break
    case 'Jazzoo 2021 Red':
      hex = '#e25b1f'
      break
    case 'Jazzoo 2021 Orange':
      hex = '#fba24f'
      break
    case 'Jazzoo 2021 Green':
      hex = '#86c891'
      break
    case 'Jazzoo 2021 Light Green':
      hex = '#b8d544'
      break
    case 'Jazzoo 2021 Blue':
      hex = '#63cbe6'
      break
    case 'Jazzoo 2022 Green':
      hex = '#2B4D20'
      break
    case 'Jazzoo 2022 Maroon':
      hex = '#A12D3A'
      break
    case 'Jazzoo 2022 Teal':
      hex = '#107977'
      break
    case 'Jazzoo 2023 Teal':
      hex = '#2BB2AF'
      break
    case 'Jazzoo 2023 Aqua':
      hex = '#54C2DA'
      break
    case 'Jazzoo 2023 Blue':
      hex = '#5475B8'
      break
    case 'Jazzoo 2023 Navy':
      hex = '#003651'
      break
    case 'Jazzoo 2023 Purple':
      hex = '#845AA1'
      break
    case 'Jazzoo 2023 Red':
      hex = '#E7202E'
      break
    case 'Jazzoo 2024 Teal':
      hex = '#66C8C8'
      break
    case 'Jazzoo 2024 Blue':
      hex = '#20C0DB'
      break
    case 'Jazzoo 2024 Pink':
      hex = '#F69689'
      break
    case 'Jazzoo 2024 Coral':
      hex = '#FBC8B0'
      break
    case 'Jazzoo 2025 Yellow':
      hex = '#E2AA00'
      break
    case 'Jazzoo 2025 Orange':
      hex = '#D55A19'
      break
    case 'Jazzoo 2025 Red':
      hex = '#CC2F02'
      break
    case 'Jazzoo 2025 Burnt Red':
      hex = '#A12501'
      break
    case 'Jazzoo 2025 Green':
      hex = '#41A028'
      break
    case 'Jazzoo 2025 Dark Green':
      hex = '#2f8c39'
      break
    default:
      hex = '#000000'
  }

  if (regex.test(s))
    return s

  return hex
}

/**
 * Converts a hex color string to its RGB components.
 * @param {string} hex - The hex color string (e.g., '#FFFFFF' or '#FFF').
 * @returns {object} An object with r, g, and b properties.
 */
function hexToRgb(hex) {
  const fullHex = hex.length === 4
    ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`
    : hex;

  const bigint = parseInt(fullHex.slice(1), 16);
  return {
    r: (bigint >> 16) & 255,
    g: (bigint >> 8) & 255,
    b: bigint & 255,
  };
}

/**
 * Calculates the relative luminance of a color.
 * @param {object} rgb - An object with r, g, and b properties.
 * @returns {number} The relative luminance.
 */
function getLuminance({ r, g, b }) {
  const toLuminance = (v) => {
    v /= 255;
    return v <= 0.03928
      ? v / 12.92
      : Math.pow((v + 0.055) / 1.055, 2.4);
  };

  const [lr, lg, lb] = [r, g, b].map(toLuminance);
  return 0.2126 * lr + 0.7152 * lg + 0.0722 * lb;
}

/**
 * Determines whether black or white text has better contrast on a given background color.
 * @param {string} hex - The background color in hex format.
 * @returns {string} The hex code for black ('#000000') or white ('#FFFFFF').
 */
function getContrastColor(hex) {
  const rgb = hexToRgb(hex);
  const luminance = getLuminance(rgb);
  return luminance > 0.179 ? '#000000' : '#FFFFFF';
}

/**
 * Returns the appropriate text color based on the background color name or hex value.
 * Predefined named colors take precedence over computed contrast colors.
 * @param {string} color - The background color name or hex value.
 * @returns {string} The hex code for the text color.
 */
export function getTextColor(color) {
  const namedTextColors = {
    'Zoo Green': '#FFFFFF',
    'Zoo Blue': '#FFFFFF',
    'Cardinal': '#FFFFFF',
    'Orchid': '#FFFFFF',
    'Tangerine': '#FFFFFF',
    'Sea': '#FFFFFF',
    'Terra Cotta': '#FFFFFF',
    'Pumpkin': '#FFFFFF',
    'Canary': '#FFFFFF',
    'White': '#000000',
  };

  if (Object.prototype.hasOwnProperty.call(namedTextColors, color)) {
    return namedTextColors[color];
  }

  const isValidHex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/i.test(color);
  if (isValidHex) {
    // console.log(getContrastColor(color));
    return getContrastColor(color);
  }

  return '#000000'; // Default fallback color
}

/**
 * Returns the attribute of an object that matches "keyString"
 * Used to get an attribute regardless of trailing numbers
 * i.e. will return data for title, title1, title2 etc...
 * @param {object} data The object to pull the attribute from
 * @param {string} keyString the key (sans trailing nubmers) of the object to return
 */
export function getAttribute(data, keyString) {
  let foundObject
  Object.keys(data)?.forEach((key) => {
    if (key.replace(/\d+$/, '') === keyString) {
      foundObject = data[key]
    }
  })
  return foundObject
}

export function renderCheckmark(value) {
  if (value === 'Yes' || value === true) {
    return <Icon icon="check" width="25" color="Black" />
  } else {
    return ''
  }
}

export function filterArrayByType(body, filterString) {
  return body
    ? body?.filter((d) => d.slice_type?.replace(/\d+$/, '') === filterString)
    : []
}

export function hasSlice(body, filterString) {
  return filterArrayByType(body, filterString).length > 0
}

export function renderAsText(value) {
  return isObject(value) ? (
    <PrismicText field={value} />
  ) : isString(value) ? (
    value
  ) : (
    ''
  )
}

export function renderVideoUrl(video) {
  if (!video) {
    console.warn("renderVideoUrl: No video provided.");
    return null;
  }

  const transformVideoUrl = (url) => {
    const youtubeRegex = /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/;
    const vimeoRegex = /^(?:https?:\/\/)?(?:www\.)?vimeo\.com\/(?:video\/)?(\d+)/;
    const dailymotionRegex = /^(?:https?:\/\/)?(?:www\.)?dailymotion\.com\/(?:video\/|embed\/video\/)([a-zA-Z0-9]+)/;

    if (youtubeRegex.test(url)) {
      const match = url.match(youtubeRegex);
      return `https://www.youtube.com/embed/${match[1]}?enablejsapi=1`;
    }

    if (vimeoRegex.test(url)) {
      const match = url.match(vimeoRegex);
      return `https://player.vimeo.com/video/${match[1]}`;
    }

    if (dailymotionRegex.test(url)) {
      const match = url.match(dailymotionRegex);
      return `https://www.dailymotion.com/embed/video/${match[1]}`;
    }

    return url;
  };

  if (isObject(video) && isString(video.url)) {
    try {
      const url = new URL(video.url).href;
      return transformVideoUrl(url);
    } catch (error) {
      console.error("renderVideoUrl: Invalid URL in video object.", error);
      return null;
    }
  }

  if (isString(video)) {
    try {
      const url = new URL(video).href;
      return transformVideoUrl(url);
    } catch (error) {
      console.error("renderVideoUrl: Invalid video string URL.", error);
      return null;
    }
  }

  console.warn("renderVideoUrl: Invalid video input.");
  return null;
}



export function renderImageUrl(image) {
  /* console.log('image ->', image) */
  return image && isObject(image) ? image.url : isString(image) ? image : ''
}

export function renderLinkUrl(link) {
  return isObject(link) ? link.url : isString(link) ? link : ''
}

export function renderAsHtml(value, defanged = false) {
  if (!value) return '';

  console.log(value);

  if (isObject(value)) {
    return (
      <PrismicRichText
        components={{
          embed: ({ node, key }) => {
            const htmlContent = defanged
              ? defangHtml(node.oembed.html ?? '')
              : node.oembed.html ?? '';

            return (
              <div
                key={key}
                data-oembed={node.oembed.embed_url}
                data-oembed-type={node.oembed.type}
                className="embedWrapper embed-youtube"
                data-oembed-provider={node.oembed.provider_name}
                dangerouslySetInnerHTML={{
                  __html: htmlContent,
                }}
              />
            );
          },
        }}
        field={value}
      />
    );
  }

  if (isString(value)) {
    return defanged ? (
      <div
        dangerouslySetInnerHTML={{
          __html: defangHtml(value),
        }}
      />
    ) : (
      value
    );
  }

  return '';
}

export function chunk(array, size) {
  const chunked_arr = []
  let copied = [...array] // ES6 destructuring
  const numOfChild = Math.ceil(copied.length / size) // Round up to the nearest integer
  for (let i = 0; i < numOfChild; i++) {
    chunked_arr.push(copied.splice(0, size))
  }
  return chunked_arr
}

/**
 * Return true if string contains a PLU.
 * @param {*} string
 */
export function isPlu(string) {
  string = renderAsText(string)
  return string.match(/\D{2}\d{5}/g) !== null
}

/**
 * Return an array containing any/all the PLUS in a string.
 * @param {*} string
 */
export function getPlus(string) {
  string = renderAsText(string)
  var plus = string.match(/\D{2}\d{5}/g)
  plus = plus?.map((plu) => plu.toUpperCase())
  return plus
}

/**
 * Return ONE plu from a string - the first if more than one.
 * @param {*} string
 */
export function getPlu(string) {
  string = renderAsText(string)
  string = string.match(/\D{2}\d{5}/g)[0]
  return string ? string.toUpperCase() : null
}

/**
 *
 * @param { array of items with plu as index and price as value} items
 * @param { string } string the plu to search for
 */
export function getPrice(items, string, digits) {
  /* console.log('DIGITS GET PRICE', digits) */
  if (items) {
    if (isPlu(string)) {
      if (getPlus(string).length > 1) {
        var plus = getPlus(string)
        /* console.log('PLUS ', plus) */
        return (
          formatPriceString(items[plus[0]], digits) +
          ' - ' +
          formatPriceString(items[plus[1]], digits)
        )
      } else {
        return formatPriceString(items[getPlu(string)], digits)
      }
    } else {
      return string
        ? formatPriceString(string, digits)
        : digits === 0
          ? '$--'
          : '$--.--'
    }
  } else {
    return string
      ? formatPriceString(string, digits)
      : digits === 0
        ? '$--'
        : '$--.--'
  }
}

/**
 * Takes a string and formats it to display as currency
 * @param { string } string
 */
export function formatPriceString(string, digits = 0) {
  /* console.log('DIGITS format', digits) */
  if (string) {
    string = string.trim()
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: digits,
    })

    //Handle price ranges
    if (string.split('-').length > 1) {
      return (
        formatPriceString(string.split('-')[0], digits) +
        ' - ' +
        formatPriceString(string.split('-')[1], digits)
      )
    }

    var formatted =
      string === 'FREE with Postcard'
        ? 'FREE with Postcard'
        : string === '0'
          ? 'FREE'
          : formatter.format(string.replace('$', '').replace(',', ''))
    return formatted !== '$NaN' ? formatted : digits === 0 ? '$--' : '$--.--'
  } else {
    return digits === 0 ? '$--' : '$--.--'
  }
}

/**
 * Returns an array of the url parameters
 * @param { } location
 */
export function getUrlVars(location) {
  var vars = {}

  var parts = !location
    ? {}
    : location.replace(/[?&]+([^=&]+)=([^&]*)/gi, function (m, key, value) {
      vars[key] = value
    })

  return vars
}

// GET THE DAY OF THE WEEK
export function getDayOfWeek(n) {
  let dayOfWeek
  switch (n) {
    case 0:
      dayOfWeek = 'Sunday'
      break
    case 1:
      dayOfWeek = 'Monday'
      break
    case 2:
      dayOfWeek = 'Tuesday'
      break
    case 3:
      dayOfWeek = 'Wednesday'
      break
    case 4:
      dayOfWeek = 'Thursday'
      break
    case 5:
      dayOfWeek = 'Friday'
      break
    case 6:
    default:
      dayOfWeek = 'Saturday'
      break
  }
  return dayOfWeek
}

// export function getActivitySeasonKey(date = moment()) {
//   const year = date.year()
//   let seasonKey

//   // WINTER
//   if (date.isBetween([year, 11, 1], [year, 2, 29], null, "[]")) {
//     seasonKey = "activity_times"
//     // SPRING
//   } else if (date.isBetween([year, 3, 1], [year, 5, 22], null, "[]")) {
//     seasonKey = "activity_times"
//     // FALL
//   } else if (date.isBetween([year, 9, 8], [year, 10, 31], null, "[]")) {
//     seasonKey = "activity_times"
//     // SUMMER
//   } else if ((date.isBetween([year, 5, 23], [year, 9, 7]), null, "[]")) {
//     seasonKey = "activity_times"
//   }

//   return seasonKey
// }

export function generateActivitiesByDate(activities, dateArg) {
  let date = isObject(dateArg) ? dateArg : moment(dateArg)
  /* console.log('DATE', date.format()) */

  let weekends = []
  let day = 1
  let month = moment(date).date(day)

  // STILL THE SAME MONTH?
  while (month.month() === date.month()) {
    let weekend = []
    let isSaturday = month.day() === 6

    // ADD SATURDAY?
    if (isSaturday) {
      weekend.push(day)
    }

    // NEXT DAY
    month.date(++day)

    if (isSaturday) {
      weekend.push(month.date()) // ADD SUNDAY
      weekends.push(weekend) // ADD WEEKEND
    }
  }
  // console.log('current month', month.month())
  // console.log('date month', date.month())
  // console.log('weekends', weekends)

  // const year = date.year()
  // let seasonKey

  // // WINTER
  // if (date.isBetween([year, 11, 1], [year, 2, 29], null, "[]")) {
  //   seasonKey = "activity_times"
  //   // SPRING
  // } else if (date.isBetween([year, 3, 1], [year, 5, 22], null, "[]")) {
  //   seasonKey = "activity_times"
  //   // FALL
  // } else if (date.isBetween([year, 9, 8], [year, 10, 31], null, "[]")) {
  //   seasonKey = "activity_times"
  //   // SUMMER
  // } else if ((date.isBetween([year, 5, 23], [year, 9, 7]), null, "[]")) {
  //   seasonKey = "activity_times"
  // }

  // /* console.log("season key:", seasonKey) */

  // BUILD ACTIVITY TIMES
  let activityTimes = []
  activities &&
    activities.length > 0 &&
    activities.forEach((activity) => {
      const activityNode =
        'data' in activity.node ? activity.node.data : activity.node

      let isActive = activityNode['activity_times_are_active']

      if (activityNode['activity_times'] && isActive === true) {
        activityNode['activity_times'].forEach((time) => {
          // THANKSGIVING OR CHRISTMAS OR NEW YEARS
          if (
            (date.month() !== 10 && date.dayOfYear() !== 26) ||
            (date.month() !== 11 && date.dayOfYear() !== 25) ||
            (date.month() !== 0 && date.dayOfYear() !== 1)
          ) {
            let hour24 =
              time.start_am_pm === 'PM' && Number(time.start_hour) < 12
                ? Number(time.start_hour) + 12
                : Number(time.start_hour)
            let minute = Number(time.start_minute)

            if (time.frequency === 'Daily') {
              let t = moment({
                hour:
                  time.start_am_pm === 'PM' && Number(time.start_hour) < 12
                    ? Number(time.start_hour) + 12
                    : Number(time.start_hour),
                minute: Number(time.start_minute),
              })
              activityTimes.push({
                title: activityNode.title,
                image: activityNode.spot_images,
                wide_images: activityNode.wide_images,
                activity_location: activityNode.activity_location,
                location: time.location,
                duration: time.duration,
                duration_time_units: time.duration_time_units,
                description: activityNode.description,
                date: moment({ hour: hour24, minute: minute }),
                link: activityNode.activity_parent_page,
                id:
                  'id' in activity.node
                    ? activity?.node?.id
                    : activity?.node?._meta?.id,
                uid:
                  'uid' in activity.node
                    ? activity?.node?.uid
                    : activity?.node?._meta?.uid,
              })
            } else if (time.frequency === 'Weekends Only') {
              weekends.forEach((d) => {
                if (date.date() === d[0] || date.date() === d[1]) {
                  activityTimes.push({
                    title: activityNode.title,
                    image: activityNode.spot_images,
                    wide_images: activityNode.wide_images,
                    activity_location: activityNode.activity_location,
                    location: time.location,
                    duration: time.duration,
                    duration_time_units: time.duration_time_units,
                    description: activityNode.description,
                    date: moment({ hour: hour24, minute: minute }),
                    link: activityNode.activity_parent_page,
                    id:
                      'id' in activity.node
                        ? activity?.node?.id
                        : activity?.node?._meta?.id,
                    uid:
                      'uid' in activity.node
                        ? activity?.node?.uid
                        : activity?.node?._meta?.uid,
                  })
                }
              })
            } else if (time.frequency === '1st & 3rd Weekends Only') {
              if (
                date.date() === weekends[0][0] ||
                date.date() === weekends[0][1] ||
                date.date() === weekends[2][0] ||
                date.date() === weekends[2][1]
              ) {
                activityTimes.push({
                  title: activityNode.title,
                  image: activityNode.spot_images,
                  wide_images: activityNode.wide_images,
                  activity_location: activityNode.activity_location,
                  location: time.location,
                  duration: time.duration,
                  duration_time_units: time.duration_time_units,
                  description: activityNode.description,
                  date: moment({ hour: hour24, minute: minute }),
                  link: activityNode.activity_parent_page,
                  id:
                    'id' in activity.node
                      ? activity?.node?.id
                      : activity?.node?._meta?.id,
                  uid:
                    'uid' in activity.node
                      ? activity?.node?.uid
                      : activity?.node?._meta?.uid,
                })
              }
            } else if (time.frequency === '2nd & 4th Weekends Only') {
              if (
                date.date() === weekends[1][0] ||
                date.date() === weekends[1][1] ||
                date.date() === weekends[3][0] ||
                date.date() === weekends[3][1]
              ) {
                activityTimes.push({
                  title: activityNode.title,
                  image: activityNode.spot_images,
                  activity_location: activityNode.activity_location,
                  location: time.location,
                  link: activityNode.activity_parent_page,
                  duration: time.duration,
                  duration_time_units: time.duration_time_units,
                  description: activityNode.description,
                  id: activity.id ? activity.meta.id : null,
                  uid: activity.uid ? activity.meta.uid : '',
                  date: moment({ hour: hour24, minute: minute }),
                })
              }
            } else if (time.frequency === 'Weekdays Only') {
              if (date.day() >= 1 && date.day() <= 5) {
                activityTimes.push({
                  title: activityNode.title,
                  image: activityNode.spot_images,
                  wide_images: activityNode.wide_images,
                  activity_location: activityNode.activity_location,
                  location: time.location,
                  duration: time.duration,
                  duration_time_units: time.duration_time_units,
                  description: activityNode.description,
                  date: moment({ hour: hour24, minute: minute }),
                  link: activityNode.activity_parent_page,
                  id:
                    'id' in activity.node
                      ? activity?.node?.id
                      : activity?.node?._meta?.id,
                  uid:
                    'uid' in activity.node
                      ? activity?.node?.uid
                      : activity?.node?._meta?.uid,
                })
              }
            }
          }
        })
      }
      // console.log('Activity Times', activityTimes)
    })

  // SORT BY TIME
  activityTimes = sortBy(activityTimes, [
    function (d) {
      return d.date.valueOf()
    },
  ])

  return activityTimes
}

export function responsiveImageUrl(data) {
  if (typeof window === 'undefined' || !data) return null

  /* console.log('Data', data) */
  const width = typeof window !== 'undefined' ? window.innerWidth : ''
  let retImage = data

  if (width >= 1200 && typeof data.xl !== 'undefined') {
    /* console.log('Responsive Image XL') */
    retImage = data.xl
  } else if (width >= 992 && typeof data.lg !== 'undefined') {
    /* console.log('Responsive Image LG') */
    retImage = data.lg
    return renderImageUrl(data.lg)
  } else if (width >= 768 && typeof data.md !== 'undefined') {
    /* console.log('Responsive Image MD') */
    retImage = data.md
    /* console.log('Responsive Image SM') */
  } else if (width < 768 && typeof data.sm !== 'undefined') {
    retImage = data.sm
  } else {
    retImage = data
  }

  return renderImageUrl(retImage)
}

export const sendEmail = (subject, text) => {
  let options = {}
  options = {
    from: 'joshua.bradley@levelfivesolutions.com',
    to: 'joshua.bradley@levelfivesolutions.com',
    subject: subject,
    html: text,
  }
  // console.log(options)

  axios.post(
    'https://us-central1-kc-zoo-map-status.cloudfunctions.net/sendemail',
    options
  );
};


/**
 * deepClone - Recursively clones an object, preserving functions.
 *
 * @param {Object} obj - The object to clone.
 * @returns {Object} - The cloned object.
 */
function deepClone(obj) {
  if (obj === null || typeof obj !== 'object') return obj;
  if (obj instanceof Function) return obj;
  if (obj instanceof Date) return new Date(obj);
  if (obj instanceof RegExp) return new RegExp(obj);

  const clonedObj = Array.isArray(obj) ? [] : {};
  for (const key in obj)
    if (obj.hasOwnProperty(key))
      clonedObj[key] = deepClone(obj[key]);
  return clonedObj;
}

/**
* deepMerge - Recursively merges two objects, preserving functions.
*
* @param {Object} target - The target object.
* @param {Object} source - The source object.
* @returns {Object} - The merged object.
*/
function deepMerge(target, source) {
  for (const key in source) {
    if (
      source[key] instanceof Object &&
      key in target &&
      target[key] instanceof Object &&
      !(source[key] instanceof Function)
    )
      deepMerge(target[key], source[key]);
    else
      target[key] = source[key];
  }
  return target;
}

/**
 * Defangs HTML by removing or escaping potentially dangerous elements and attributes.
 * Utilizes multiple sequential regular expressions for each sanitization rule.
 * Works in both server and client environments without external dependencies.
 *
 * @param {string} value - The HTML string to sanitize.
 * @param {object} config - Configuration options for sanitization.
 * @returns {string} - The sanitized HTML string.
 */
export function defangHtml(value, config = {}) {
  if (!value || typeof value !== "string") return value;

  let sanitized = value;

  if (config.debug) console.log("🔶 Starting defangHtml 🔶\n");

  // Define default rules
  const defaultRules = {
    sanitizeScripts: {
      enabled: true,
      mode: "remove", // Options: 'remove', 'escape'
      regex: [
        /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi
      ],
    },
    sanitizeJavascriptHrefs: {
      enabled: true,
      mode: "replace", // Options: 'remove', 'escape', 'replace'
      regex: [
        /href\s*=\s*"javascript:[^"]*"/gi, // Double quotes
        /href\s*=\s*'javascript:[^']*'/gi, // Single quotes
        /href\s*=\s*javascript:[^\s>]+/gi   // No quotes
      ],
      replacement: 'href="#"',
    },
    sanitizeInlineEvents: {
      enabled: true,
      mode: "remove", // Options: 'remove', 'escape'
      regex: [
        / on\w+="[^"]*"/gi, // Double quotes
        / on\w+='[^']*'/gi, // Single quotes
        / on\w+=\S+/gi       // No quotes
      ],
    },
    sanitizeIframes: {
      enabled: true,
      mode: "remove",
      regex: [
        /<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi
      ],
    },
    sanitizeObjects: {
      enabled: true,
      mode: "remove",
      regex: [
        /<object\b[^<]*(?:(?!<\/object>)<[^<]*)*<\/object>/gi
      ],
    },
    sanitizeEmbeds: {
      enabled: true,
      mode: "remove",
      regex: [
        /<embed\b[^<]*(?:(?!<\/embed>)<[^<]*)*<\/embed>/gi
      ],
    },
    sanitizeStyles: {
      enabled: true,
      mode: "remove",
      regex: [
        /<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi
      ],
    },
    sanitizeLinks: {
      enabled: true,
      mode: "remove",
      regex: [
        /<link\b[^<]*\/?>/gi
      ],
    },
    sanitizeComments: {
      enabled: false,
      mode: "remove",
      regex: [
        /<!--[\s\S]*?-->/g
      ],
    },
  };

  // Merge user-defined rules with default rules
  const userRules = config.rules || {};
  const rules = {};

  // Deep merge: For each rule, if user provides regex as array or single, merge accordingly
  for (const [ruleName, ruleConfig] of Object.entries(defaultRules)) {
    if (userRules.hasOwnProperty(ruleName)) {
      rules[ruleName] = { ...ruleConfig, ...userRules[ruleName] };
      // Ensure regex is always an array
      if (!Array.isArray(rules[ruleName].regex)) {
        rules[ruleName].regex = [rules[ruleName].regex];
      }
    } else {
      // Use default rule
      rules[ruleName] = { ...ruleConfig };
      // Ensure regex is always an array
      if (!Array.isArray(rules[ruleName].regex))
        rules[ruleName].regex = [rules[ruleName].regex];
    }
  }

  if (config.debug)
    console.log("🔶 Merged Rules:", rules);

  // Apply each rule
  for (const [name, rule] of Object.entries(rules)) {
    if (!rule.enabled) {
      if (config.debug) console.log(`\n❌ Rule "${name}" is disabled.\n`);
      continue;
    }

    if (rule.regex) {
      rule.regex.forEach((regex) => {
        if (config.debug) {
          const matches = sanitized.match(regex);
          const count = matches ? matches.length : 0;
          if (count > 0)
            console.log(`✅ Rule "${name}" matched ${count} time(s).`);
        }

        // Apply replacement based on mode
        if (rule.mode === "remove") {
          sanitized = sanitized.replace(regex, "");
        } else if (rule.mode === "escape") {
          sanitized = sanitized.replace(regex, (match) =>
            match
              .replace(/</g, "&lt;")
              .replace(/>/g, "&gt;")
              .replace(/"/g, "&quot;")
              .replace(/'/g, "&#39;")
          );
        } else if (rule.mode === "replace" && rule.replacement)
          sanitized = sanitized.replace(regex, rule.replacement);

        if (config.debug)
          console.log(`🔄 Applied rule: "${name}" (mode: "${rule.mode}")\n`);
      });
    }
  }

  // Additional sanitization: Remove any remaining style attributes to prevent CSS-based attacks
  if (rules.sanitizeStyles.enabled) {
    sanitized = sanitized.replace(/ style=["'][^"']*["']/gi, "");
    if (config.debug) console.log('🔄 Removed "style" attributes.\n');
  }

  // Optionally escape remaining < and > characters
  if (config.escapeRemaining) {
    sanitized = sanitized.replace(/</g, "&lt;").replace(/>/g, "&gt;");
    if (config.debug) console.log('🔄 Escaped remaining "<" and ">" characters.\n');
  }

  // Trim and collapse whitespace
  sanitized = sanitized.replace(/\s{2,}/g, " ").trim();

  return sanitized;
}




/**
 * Transforms the price string by:
 * - Optionally appending a symbol in a <sup> element
 * - Replacing hyphen with an en dash
 * - Handling "free", ranges, and comma-separated prices
 * @param {string} price - The original price string.
 * @param {boolean} prependSymbol - Whether to prepend the symbol in a <sup> element (default: true).
 * @param {string} symbol - The symbol to process (default: '$').
 * @returns {string} - Transformed HTML string for the price.
 */
export function formatPrice(price, prependSymbol = false, symbol = '$') {
  if (!price) return '';

  // Match single numbers, ranges, or "free"
  const priceRegex = /(\bfree\b|\$?\d+)(\s*-\s*)(\bfree\b|\$?\d+)?|(\bfree\b|\$?\d+)/gi;

  return price.replace(priceRegex, (match, rangeStart, rangeSeparator, rangeEnd, singleValue) => {
    if (singleValue) {
      // Handle single values (e.g., "free" or "$100")
      if (singleValue.toLowerCase() === 'free')
        return 'free';

      return prependSymbol || singleValue.startsWith(symbol)
        ? `<sup class="sup-currency-symbol">${symbol}</sup>${singleValue.replace(symbol, '')}`
        : singleValue;
    }

    if (rangeStart && rangeSeparator && rangeEnd) {
      // Handle ranges (e.g., "free - $100" or "$100 - free")
      const formattedStart = rangeStart.toLowerCase() === 'free'
        ? 'free'
        : prependSymbol || rangeStart.startsWith(symbol)
          ? `<sup class="sup-currency-symbol">${symbol}</sup>${rangeStart.replace(symbol, '')}`
          : rangeStart;

      const formattedEnd = rangeEnd.toLowerCase() === 'free'
        ? 'free'
        : prependSymbol || rangeEnd.startsWith(symbol)
          ? `<sup class="sup-currency-symbol">${symbol}</sup>${rangeEnd.replace(symbol, '')}`
          : rangeEnd;

      return `${formattedStart} – ${formattedEnd}`;
    }

    return match; // Fallback for any unmatched case
  });
}
